import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { card, h1, btnPrimary, waLink, badge, fmtDate } from "./ui";
import DeletePatientModal from "./DeletePatientModal";
import PatientFormDialog from "./PatientFormDialog";
import PatientDocument from "./PatientDocument";
import DocTemplateDialog from "./DocTemplateDialog";
import { Plus, Pencil, Trash2, Phone, User, FolderOpen, MessageCircle, FileText, AlertTriangle } from "lucide-react";

const DOC_LABEL = {
  nao_preparado: "doc não preparado",
  dados_pendentes: "doc com dados pendentes",
  pronto_para_revisao: "doc pronto p/ revisão",
  aguardando_envio: "doc aguardando envio",
  enviado: "doc enviado",
};

export default function Patients() {
  const [items, setItems] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [docTarget, setDocTarget] = useState(null);
  const [tplOpen, setTplOpen] = useState(false);

  const load = () => api.get("/patients").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditTarget(null); setFormOpen(true); };
  const openEdit = (p) => { setEditTarget(p); setFormOpen(true); };

  return (
    <div data-testid="patients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="font-body text-xs tracking-[0.35em] uppercase text-[#C87A5E]">Pacientes</p>
          <h1 className={h1}>Meus pacientes</h1>
        </div>
        <div className="flex gap-2">
          <button className="inline-flex items-center gap-2 rounded-full border border-[#5B6E5B]/20 bg-white px-4 py-2 text-sm text-[#4A5D4E] hover:bg-[#F5F0E6] transition-colors"
            onClick={() => setTplOpen(true)} data-testid="doc-template-btn">
            <FileText className="w-4 h-4" /> Texto-base do documento
          </button>
          <button className={btnPrimary} onClick={openNew} data-testid="new-patient-btn"><Plus className="w-4 h-4" /> Novo paciente</button>
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && <p className="font-body text-sm text-[#7A8A7A]">Nenhum paciente cadastrado ainda.</p>}
        {items.map((p) => {
          const c = p.completeness || {};
          const ds = p.doc_status || {};
          return (
            <div key={p.id} className={`${card} p-6`} data-testid="patient-card">
              <div className="flex items-start justify-between">
                <div className="grid place-items-center w-11 h-11 rounded-full bg-[#F5F0E6] mb-4">
                  <User className="w-5 h-5 text-[#5B6E5B]" strokeWidth={1.5} />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setDocTarget(p)} className="text-[#5B6E5B] hover:text-[#2C332A]" data-testid="patient-document" title="Documento informativo e condições">
                    <FileText className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEdit(p)} className="text-[#5B6E5B] hover:text-[#2C332A]" data-testid="edit-patient"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => setDeleteTarget(p)} className="text-[#C87A5E] hover:text-[#B2644D]" data-testid="delete-patient" title="Apagar paciente e todos os dados"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="font-display text-xl text-[#2C332A]">{p.name}</p>
              {p.phone && (
                <p className="font-body text-sm text-[#7A8A7A] mt-2 flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5" /> {p.phone}
                  <a href={waLink(p.phone)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid="patient-whatsapp"
                    className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#25D366]/12 px-2.5 py-1 text-xs text-[#128C4A] hover:bg-[#25D366]/20 transition-colors">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                </p>
              )}
              <div className="flex flex-wrap gap-1.5 mt-3">
                <span className={badge(c.complete)} data-testid="patient-completeness">
                  {c.complete ? "Cadastro completo" : `Cadastro incompleto · ${c.missing_count} pendente(s)`}
                </span>
                <span className={badge(ds.status === "enviado")} data-testid="patient-doc-status" title={ds.last_sent_at ? `Último envio: ${fmtDate(ds.last_sent_at.slice(0, 10))}` : ""}>
                  {DOC_LABEL[ds.status] || "doc não preparado"}
                </span>
              </div>
              {c.emergency_missing && (
                <p className="mt-2 flex items-start gap-1.5 font-body text-[11px] text-[#B2644D]" data-testid="patient-emergency-missing">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Contato de emergência pendente — regularize antes do primeiro atendimento.
                </p>
              )}
              <Link to={`/dashboard/pacientes/${p.id}`} data-testid="open-prontuario-btn" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#5B6E5B] px-4 py-2 text-sm text-[#FDFBF7] hover:bg-[#4A5D4E] transition-colors">
                <FolderOpen className="w-4 h-4" /> Abrir prontuário
              </Link>
            </div>
          );
        })}
      </div>

      <PatientFormDialog open={formOpen} onOpenChange={setFormOpen} patient={editTarget} onSaved={load} />
      <PatientDocument patient={docTarget} onClose={() => { setDocTarget(null); load(); }} />
      <DocTemplateDialog open={tplOpen} onOpenChange={setTplOpen} />
      <DeletePatientModal patient={deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={() => { setDeleteTarget(null); load(); }} />
    </div>
  );
}
